import { PSEO_LENDERS, formatKr, monthlyCost, type PseoLender } from "@/lib/pseo";

/** Finare beloppsstege än startsidans – används bara för kombinationssidor. */
function matrixAmountSteps(): number[] {
  const steps: number[] = [2000, 3000, 4000];
  for (let a = 5000; a < 100000; a += 5000) steps.push(a);
  for (let a = 100000; a < 300000; a += 10000) steps.push(a);
  for (let a = 300000; a <= 600000; a += 25000) steps.push(a);
  return steps;
}

export type MatrixCategory = {
  slug: string;
  /** Kort namn i rubriker, t.ex. "utan UC" */
  label: string;
  /** Namn i brödsmulor */
  crumb: string;
  minAmount: number;
  maxAmount: number;
  filter: (l: PseoLender) => boolean;
  h1: (kr: string) => string;
  title: (kr: string) => string;
  description: (kr: string) => string;
  intro: (kr: string) => string;
  faq: (amount: number, kr: string) => { q: string; a: string }[];
};

/** Max antal genererade kombinationssidor (skyddar byggstorleken). */
export const MAX_MATRIX_PAGES = 2000;

/** Minsta antal matchande långivare för att en sida ska indexeras. */
export const MIN_LENDERS_FOR_INDEX = 3;

export const MATRIX_CATEGORIES: MatrixCategory[] = [
  {
    slug: "lan-utan-uc",
    label: "utan UC",
    crumb: "Lån utan UC",
    minAmount: 5000,
    maxAmount: 200000,
    filter: (l) => l.allowsUC === false,
    h1: (kr) => `Lån utan UC ${kr} – jämför långivare`,
    title: (kr) => `Lån utan UC ${kr} – jämför långivare 2026 | Svensk Låneguide`,
    description: (kr) =>
      `Vill du låna ${kr} utan UC? Jämför långivare som använder Creditsafe eller Bisnode i stället för UC – se ränta, månadskostnad och villkor.`,
    intro: (kr) =>
      `Här jämför du långivare som kan bevilja ${kr} utan att ta en kreditupplysning hos UC. Långivarna nedan använder andra kreditupplysningsbolag, men gör alltid en kreditprövning innan lånet beviljas.`,
    faq: (amount, kr) => [
      {
        q: `Kan jag låna ${kr} helt utan UC?`,
        a: `Ja, flera långivare på den här sidan hämtar kreditupplysning via Creditsafe eller Bisnode i stället för UC. Din ansökan syns då inte som en UC-förfrågan, men en kreditprövning görs alltid.`,
      },
      {
        q: `Hur snabbt får jag pengarna?`,
        a: `Flera av långivarna betalar ut ${kr} samma dag om du signerar med BankID och har konto i en svensk bank. Ansöker du utanför bankernas öppettider kan utbetalningen ske nästa bankdag.`,
      },
      {
        q: `Vad kostar det att låna ${kr} utan UC?`,
        a: `Lån utan UC har normalt högre ränta än vanliga privatlån, ofta 19–29 procent. På ${kr} över 5 år innebär en ränta på 22 procent ungefär ${formatKr(monthlyCost(amount, 5, 22))} per månad före avgifter.`,
      },
      {
        q: `Påverkas mitt kreditbetyg?`,
        a: `Förfrågan registreras hos det kreditupplysningsbolag långivaren använder, men inte hos UC. Själva lånet påverkar ändå din totala skuldsättning och därmed framtida låneansökningar.`,
      },
    ],
  },
  {
    slug: "lan-med-betalningsanmarkning",
    label: "med betalningsanmärkning",
    crumb: "Lån med betalningsanmärkning",
    minAmount: 5000,
    maxAmount: 400000,
    filter: (l) => l.allowsBadCredit === true,
    h1: (kr) => `Lån med betalningsanmärkning ${kr}`,
    title: (kr) => `Lån ${kr} med betalningsanmärkning – jämför långivare | Svensk Låneguide`,
    description: (kr) =>
      `Jämför långivare som kan bevilja ${kr} trots betalningsanmärkning. Se krav, räntor och månadskostnad – ansök kostnadsfritt online.`,
    intro: (kr) =>
      `En betalningsanmärkning betyder inte automatiskt avslag. Nedan jämför du långivare och förmedlare som gör en helhetsbedömning av din ekonomi och kan bevilja ${kr} trots anmärkning.`,
    faq: (amount, kr) => [
      {
        q: `Går det att låna ${kr} med betalningsanmärkning?`,
        a: `Ja, men kraven är hårdare. Långivarna nedan tittar på din nuvarande inkomst och betalningsförmåga snarare än enbart på anmärkningen. En fast inkomst ökar chansen betydligt.`,
      },
      {
        q: `Behöver jag medsökande?`,
        a: `Nej, men en medsökande med god kreditvärdighet ökar chansen att bli beviljad ${kr} och kan sänka räntan påtagligt.`,
      },
      {
        q: `Vad blir månadskostnaden?`,
        a: `Med en ränta runt 20 procent och 5 års löptid landar ${kr} på cirka ${formatKr(monthlyCost(amount, 5, 20))} per månad före uppläggnings- och aviavgifter.`,
      },
      {
        q: `Kan jag söka om jag har skuld hos Kronofogden?`,
        a: `Pågående skuldsaldo hos Kronofogden leder oftast till avslag. Är skulden reglerad och anmärkningen äldre är chansen större.`,
      },
    ],
  },
  {
    slug: "samla-lan",
    label: "samlingslån",
    crumb: "Samla lån",
    minAmount: 50000,
    maxAmount: 600000,
    filter: (l) => l.maxAmount >= 100000,
    h1: (kr) => `Samla lån ${kr} – sänk månadskostnaden`,
    title: (kr) => `Samla lån ${kr} – jämför samlingslån 2026 | Svensk Låneguide`,
    description: (kr) =>
      `Samla dyra krediter till ${kr} med lägre ränta. Jämför långivare för samlingslån, se månadskostnad och ansök med en enda kreditupplysning.`,
    intro: (kr) =>
      `Har du flera dyra krediter och smslån? Med ett samlingslån på ${kr} ersätter du dem med ett enda lån till lägre ränta – och får en avi i stället för många.`,
    faq: (amount, kr) => [
      {
        q: `Hur mycket kan jag spara på att samla ${kr}?`,
        a: `Om du i dag betalar 20 procent ränta och i stället får 9 procent sjunker månadskostnaden på ${kr} från cirka ${formatKr(monthlyCost(amount, 5, 20))} till cirka ${formatKr(monthlyCost(amount, 5, 9))} vid 5 års löptid.`,
      },
      {
        q: `Löser långivaren mina gamla lån automatiskt?`,
        a: `Ja, de flesta banker betalar direkt till dina befintliga kreditgivare när du bifogar skuldunderlag. Du behöver bara ange kontonummer och saldon.`,
      },
      {
        q: `Behöver jag säkerhet för ${kr}?`,
        a: `Nej, samlingslån är blancolån utan säkerhet. Räntan baseras på din inkomst, dina nuvarande krediter och din betalningshistorik.`,
      },
      {
        q: `Hur många kreditupplysningar tas?`,
        a: `Ansöker du via en låneförmedlare tas endast en kreditupplysning, oavsett hur många banker som lämnar bud på ditt samlingslån.`,
      },
    ],
  },
  {
    slug: "snabblan",
    label: "snabblån",
    crumb: "Snabblån",
    minAmount: 2000,
    maxAmount: 100000,
    filter: (l) => l.minAmount <= 25000,
    h1: (kr) => `Snabblån ${kr} – pengar samma dag`,
    title: (kr) => `Snabblån ${kr} – utbetalning samma dag | Svensk Låneguide`,
    description: (kr) =>
      `Behöver du låna ${kr} snabbt? Jämför långivare med direkt besked och utbetalning samma dag. Se ränta, avgifter och månadskostnad.`,
    intro: (kr) =>
      `Behöver du ${kr} snabbt? Nedan jämför du långivare med helt digital ansökan, besked på minuter och utbetalning samma bankdag.`,
    faq: (amount, kr) => [
      {
        q: `Hur snabbt kan jag få ${kr} utbetalt?`,
        a: `Flera långivare betalar ut inom 15 minuter om du har konto i samma bank, annars nästa bankdag. Signering sker med BankID.`,
      },
      {
        q: `Vilka krav ställs?`,
        a: `Du behöver normalt vara minst 20 år, ha svensk folkbokföring, BankID och en regelbunden inkomst. Kraven varierar mellan långivarna nedan.`,
      },
      {
        q: `Vad kostar ett snabblån på ${kr}?`,
        a: `Vid 20 procent ränta och 3 års löptid blir månadskostnaden cirka ${formatKr(monthlyCost(amount, 3, 20))} före uppläggnings- och aviavgifter.`,
      },
      {
        q: `Kan jag lösa lånet i förtid?`,
        a: `Ja, enligt konsumentkreditlagen har du alltid rätt att lösa ett snabblån i förtid och slipper då resterande räntekostnad.`,
      },
    ],
  },
];

export function getMatrixCategory(slug: string) {
  return MATRIX_CATEGORIES.find((c) => c.slug === slug) ?? null;
}

export function lendersForMatrix(cat: MatrixCategory, amount: number) {
  return PSEO_LENDERS.filter(
    (l) => cat.filter(l) && l.minAmount <= amount && l.maxAmount >= amount,
  ).sort((a, b) => b.rating - a.rating || a.minInterest - b.minInterest);
}

/** Belopp som ger en tillräckligt innehållsrik sida för kategorin. */
export function amountsForCategory(cat: MatrixCategory): number[] {
  return matrixAmountSteps().filter(
    (a) =>
      a >= cat.minAmount &&
      a <= cat.maxAmount &&
      lendersForMatrix(cat, a).length >= MIN_LENDERS_FOR_INDEX,
  );
}

export type MatrixPage = { category: string; amount: number; slug: string };

export function allMatrixPages(): MatrixPage[] {
  const pages: MatrixPage[] = [];
  for (const cat of MATRIX_CATEGORIES) {
    for (const amount of amountsForCategory(cat)) {
      if (pages.length >= MAX_MATRIX_PAGES) return pages;
      pages.push({ category: cat.slug, amount, slug: `${amount}-kr` });
    }
  }
  return pages;
}

/** Närliggande belopp inom samma kategori, för intern länkning. */
export function neighbourAmounts(cat: MatrixCategory, amount: number, count = 6): number[] {
  const list = amountsForCategory(cat);
  const i = list.indexOf(amount);
  if (i === -1) return list.slice(0, count);
  const out: number[] = [];
  let left = i - 1;
  let right = i + 1;
  while (out.length < count && (left >= 0 || right < list.length)) {
    if (right < list.length) out.push(list[right++]!);
    if (out.length < count && left >= 0) out.push(list[left--]!);
  }
  return out.sort((a, b) => a - b);
}

/** Standardbelopp på ort-sidor: mitt i kategorins spann, men aldrig över 100 000 kr. */
export function cityAmountFor(cat: MatrixCategory): number {
  const list = amountsForCategory(cat);
  const preferred = list.filter((a) => a <= 100000);
  const pool = preferred.length ? preferred : list;
  return pool[Math.floor(pool.length / 2)] ?? cat.minAmount;
}

/** Kategorier som har tillräckligt med matchande långivare för ort-sidor. */
export function cityIndexableCategories(): MatrixCategory[] {
  return MATRIX_CATEGORIES.filter(
    (cat) => lendersForMatrix(cat, cityAmountFor(cat)).length >= MIN_LENDERS_FOR_INDEX,
  );
}

